/**
 * The v2 stage vocabulary.
 *
 * Deliberately local to `packages/web/src/v2` rather than added to
 * `@patent-studio/api-types`'s pipeline tree: that tree feeds `reduce-events.ts`,
 * `ExecutionProgressBubble`'s totals, so adding planning stages there would
 * silently change v1 progress totals.
 */

export type V2StageId =
  | "intake"
  | "thesis"
  | "claim-strategy"
  | "figures"
  | "planning-outline"
  | "generate";

/** Intake is two pages: the materials, then their classification. */
export type IntakeSubStepId = "materials" | "classification";

export type FigureSubStepId = "upload" | "define" | "review-insert";

/** Thesis is two pages: what the invention is, then whether it is novel. */
export type ThesisSubStepId = "invention" | "novelty";

/** Generate Patent Draft is two pages: the review before the start, then the draft. */
export type GenerateSubStepId = "review" | "ready";

export type SubStepId =
  IntakeSubStepId | FigureSubStepId | ThesisSubStepId | GenerateSubStepId;

/** The novelty page's tabs, in the design's words. */
export type NoveltyTabId = "positions" | "prior-art" | "analysis";

export type TabId = NoveltyTabId;

/**
 * One tab of a page that is walked in tabs: a level below a stage's steps,
 * drawn across the top of the page rather than listed in the rail.
 */
export interface PageTab<Id extends TabId = TabId> {
  id: Id;
  label: string;
  /** Held here, not decided per call site, so a tab is a destination everywhere or nowhere. */
  built: boolean;
}

export interface TabGroup {
  /** The strip's accessible name. */
  label: string;
  tabs: readonly PageTab[];
}

export interface SubStep<Id extends SubStepId = SubStepId> {
  id: Id;
  label: string;
  /** Held here, not decided per call site, so a step is a destination everywhere or nowhere. */
  built: boolean;
  /** The tabs this page is walked in, for a page that has any. */
  tabs?: TabGroup;
}

/**
 * A stage's own steps, and the words the rail counts them in.
 *
 * The words travel with the steps because they differ: the figure engine has
 * steps, the thesis has pages, and a footer that called both the same thing
 * would be reading the design back wrong in one of the two.
 */
export interface SubStepGroup {
  /** What the footer calls the whole run. */
  title: string;
  /** What the footer calls one of them. */
  noun: string;
  steps: readonly SubStep[];
}

export interface V2Stage {
  id: V2StageId;
  label: string;
  group: "planning" | "downstream";
  /** The steps this stage is made of, for a stage that has any. */
  subSteps?: SubStepGroup;
}

/** Intake's steps: the materials, then the CPC codes confirmed from them. */
export const INTAKE_SUB_STEPS: readonly SubStep<IntakeSubStepId>[] = [
  { id: "materials", label: "Upload Materials", built: true },
  { id: "classification", label: "Classification", built: true },
];

/** The figure engine's steps. */
export const FIGURE_SUB_STEPS: readonly SubStep<FigureSubStepId>[] = [
  { id: "upload", label: "Upload & Organise", built: true },
  { id: "define", label: "Define & Describe Figures", built: true },
  { id: "review-insert", label: "Review Figures", built: true },
];

/**
 * The novelty page's tabs: the positions first, then the library they are
 * assessed against, then the analysis, where each position is confirmed.
 *
 * A route answers for each, which is what `built` records. Whether the attorney
 * may walk onto one is a different question and a served one: the guard over a
 * tab reads the gate the server projects and sends them back where it is shut.
 */
export const NOVELTY_TABS: readonly PageTab[] = [
  { id: "positions", label: "Define Positions", built: true },
  { id: "prior-art", label: "Add Prior Art", built: true },
  { id: "analysis", label: "Novelty Analysis", built: true },
];

/**
 * The thesis's pages.
 *
 * Novelty is reachable because a route answers for it, even though what answers
 * is a stub saying it is not built. The old novelty URL redirects onto that
 * page, so an unreachable rail row would strand
 * an attorney on a page they cannot navigate back to. `built` says whether
 * something answers, not whether it is finished.
 */
export const THESIS_SUB_STEPS: readonly SubStep<ThesisSubStepId>[] = [
  {
    id: "invention",
    label: "Invention",
    built: true,
  },
  {
    id: "novelty",
    label: "Novelty",
    built: true,
    tabs: { label: "Novelty steps", tabs: NOVELTY_TABS },
  },
];

/**
 * Generate Patent Draft's pages. Processing is not one: it is what the second
 * page shows while the run is in flight, so the rail names only where the
 * attorney starts and where the draft arrives.
 */
export const GENERATE_SUB_STEPS: readonly SubStep<GenerateSubStepId>[] = [
  { id: "review", label: "Review before you generate", built: true },
  { id: "ready", label: "Your patent draft is ready", built: true },
];

/**
 * The draft's step in the rail, as its latest run stands. v0.28 names only the
 * ready draft; the other states take the same shape, so the step never says a
 * draft is ready while it is being made or after it failed.
 */
export function draftStepLabel(
  state: "queued" | "running" | "ready" | "stopped" | "failed" | null,
): string {
  switch (state) {
    case null:
      return "Your patent draft";
    case "queued":
    case "running":
      return "Your patent draft is being created";
    case "ready":
      return "Your patent draft is ready";
    case "stopped":
      return "Your patent draft stopped";
    case "failed":
      return "Your patent draft failed";
  }
}

export const V2_STAGES: readonly V2Stage[] = [
  {
    id: "intake",
    label: "Intake",
    group: "planning",
    subSteps: { title: "Intake", noun: "step", steps: INTAKE_SUB_STEPS },
  },
  // Figures comes before the thesis and claim strategy because the claims
  // read the numbered components the figures define.
  {
    id: "figures",
    label: "Figures",
    group: "planning",
    subSteps: {
      title: "Figure Engine",
      noun: "step",
      steps: FIGURE_SUB_STEPS,
    },
  },
  {
    id: "thesis",
    label: "Thesis",
    group: "planning",
    subSteps: { title: "Thesis", noun: "page", steps: THESIS_SUB_STEPS },
  },
  { id: "claim-strategy", label: "Claim strategy", group: "planning" },
  { id: "planning-outline", label: "Specification", group: "planning" },
  {
    id: "generate",
    label: "Generate Patent Draft",
    group: "downstream",
    subSteps: {
      title: "Generate Patent Draft",
      noun: "page",
      steps: GENERATE_SUB_STEPS,
    },
  },
];

/**
 * A page's walkthrough, as the design titles it: the page's own words set in
 * italic inside the words around them, "How *Intake* works".
 */
export interface Guide {
  lead?: string;
  topic: string;
  trail?: string;
}

/** The pages that have a walkthrough, one key per title the design gives. */
export type GuideId =
  | "home"
  | "intake"
  | "figures-upload"
  | "figures-define"
  | "figures-review"
  | "invention"
  | "prior-art-and-positions"
  | "novelty-analysis"
  | "claim-strategy"
  | "specification"
  | "generate";

export const GUIDES: Record<GuideId, Guide> = {
  home: { lead: "Introduction to ", topic: "Praxis" },
  intake: { lead: "How ", topic: "Intake", trail: " works" },
  "figures-upload": { lead: "How ", topic: "Figures", trail: " works" },
  "figures-define": { lead: "How to ", topic: "Define & Describe Figures" },
  "figures-review": { lead: "Reviewing ", topic: "Figures" },
  invention: { lead: "How ", topic: "Thesis", trail: " Works" },
  "prior-art-and-positions": {
    lead: "Adding ",
    topic: "Prior Art & Positions",
  },
  "novelty-analysis": { topic: "Assessing Novelty of the Invention" },
  "claim-strategy": { lead: "How ", topic: "Claim Strategy", trail: " works" },
  specification: { lead: "How ", topic: "Specification", trail: " works" },
  generate: { topic: "Generating the Patent Draft" },
};

/** Where a stage lives. Every link that points at one is written from here. */
export function stagePath(taskId: string, stageId: V2StageId): string {
  return `/drafts/${taskId}/${stageId}`;
}

/** Where Open Questions lives in a draft. */
export function openQuestionsPath(taskId: string): string {
  return `/drafts/${taskId}/questions`;
}

/** Where a draft opens: the stage it was last open at, or Intake for one never opened. */
export function draftOpeningPath(draft: {
  task_id: string;
  last_stage: V2StageId | null;
}): string {
  return stagePath(draft.task_id, draft.last_stage ?? "intake");
}

/**
 * Where a sub-step lives. Every link that points at one is written from here.
 *
 * Uniform across stages by construction: a step's route is its stage's route
 * with the step appended, so a stage with steps needs no path of its own.
 */
export function subStepPath(
  taskId: string,
  stageId: V2StageId,
  id: SubStepId,
): string {
  return `${stagePath(taskId, stageId)}/${id}`;
}

/**
 * The route a step can be reached at, or nothing while it is not built or there
 * is no draft open. An unbuilt step is progress, not a destination; a link at it
 * would land on a route that redirects away.
 */
export function stepDestination(
  step: SubStep,
  taskId: string | undefined,
  stageId: V2StageId,
): string | undefined {
  return step.built && taskId !== undefined
    ? subStepPath(taskId, stageId, step.id)
    : undefined;
}

/**
 * Where a page's tab lives: the page's route with the tab appended, so a tab
 * needs no path of its own any more than a step does.
 */
export function tabPath(
  taskId: string,
  stageId: V2StageId,
  stepId: SubStepId,
  tabId: TabId,
): string {
  return `${subStepPath(taskId, stageId, stepId)}/${tabId}`;
}

/** The route a tab can be reached at, or nothing while it is not built or there is no draft open. */
export function tabDestination(
  tab: PageTab,
  taskId: string | undefined,
  stageId: V2StageId,
  stepId: SubStepId,
): string | undefined {
  return tab.built && taskId !== undefined
    ? tabPath(taskId, stageId, stepId, tab.id)
    : undefined;
}

/** The only stages that carry a position. */
const PLANNING_STAGES = V2_STAGES.filter((stage) => stage.group === "planning");

/**
 * The steps a stage is made of, or nothing for a stage made of none.
 *
 * Asked of the stage rather than tested for by name, so a third stage with
 * steps needs no change to the rail that renders them.
 */
export function subStepsOf(
  id: V2StageId | undefined,
): SubStepGroup | undefined {
  return V2_STAGES.find((stage) => stage.id === id)?.subSteps;
}

/**
 * The stage after this one, or nothing after the last.
 *
 * Read over every stage, in the order the rail numbers them, so a page that
 * names the stage ahead and the rail cannot disagree, and Specification's
 * Continue reaches Generate.
 */
export function stageAfter(id: V2StageId): V2Stage | undefined {
  const at = V2_STAGES.findIndex((stage) => stage.id === id);
  return at === -1 ? undefined : V2_STAGES.at(at + 1);
}

/**
 * Where continuing out of a stage lands: the first step of the stage after it,
 * or that stage itself where it has no steps, or nothing past the last one.
 */
export function destinationAfter(
  taskId: string,
  id: V2StageId,
): string | undefined {
  const next = stageAfter(id);
  if (next === undefined) return undefined;
  const first = next.subSteps?.steps[0];
  return first === undefined
    ? stagePath(taskId, next.id)
    : stepDestination(first, taskId, next.id);
}

/**
 * 1-based position within the planning subset, or `null` outside it.
 *
 * Every number the rail shows comes from here, which is what makes
 * "Stage 5 of 4" unconstructible rather than merely untested.
 */
export function planningPosition(id: V2StageId | undefined): number | null {
  const index = PLANNING_STAGES.findIndex((stage) => stage.id === id);
  return index === -1 ? null : index + 1;
}

/**
 * The rail's position footer, or `null` when there is no planning stage to
 * count. The wireframe shows no footer in that state, so the element is absent
 * rather than blank.
 *
 * A sub-step counts within its own stage, and only within its own: a step id
 * belonging to another stage is not a position here and falls back to the stage
 * count, so a stale id cannot produce "page 3 of 2".
 */
export function railFooterLabel(
  activeId: V2StageId | undefined,
  subStepId?: SubStepId,
): string | null {
  const group = subStepsOf(activeId);
  if (group !== undefined && subStepId !== undefined) {
    const at = group.steps.findIndex((step) => step.id === subStepId);
    if (at !== -1) {
      return `${group.title} · ${group.noun} ${String(at + 1)} of ${String(group.steps.length)}`;
    }
  }
  const position = planningPosition(activeId);
  return position === null
    ? null
    : `Stage ${String(position)} of ${String(PLANNING_STAGES.length)}`;
}

/**
 * The small label above a stage pane's heading: "Stage 1"…"Stage 5", or
 * "Downstream". Not `stage.label` ("Intake"), which names the stage itself.
 */
export function stageHeadingLabel(id: V2StageId): string {
  const position = planningPosition(id);
  return position === null ? "Downstream" : `Stage ${String(position)}`;
}

/** The stage with this id, or `undefined` if the string is not a stage id. */
export function findStage(id: string | undefined): V2Stage | undefined {
  return V2_STAGES.find((stage) => stage.id === id);
}
